/**
 * Turn a rejected api call into something a site owner can act on.
 *
 * The editor used to render `reason?.payload?.error || String(reason)` straight
 * into a red toast. When an upstream service answered with a JSON body, an HTML
 * error page, or a stack trace, that is exactly what the owner saw — which is
 * why a failed assistant request looked like the browser had broken rather than
 * like the product had something to say.
 *
 * Rule: pass through only text that reads as a sentence we wrote. Anything
 * machine-shaped, multi-line, or novel-length falls back to product copy, and
 * the real detail goes to the console for support.
 */
export function friendlyError(reason: unknown, fallback: string): string {
  const err = reason as { status?: number; payload?: { error?: unknown; paywall?: unknown } } | null;
  // A 402 is a paywall, and the server's message is written for the owner
  // ("You've used all your edits — upgrade…"). Always surface it verbatim, even
  // if it ever grows past the length gate below.
  if (err?.status === 402 && typeof err.payload?.error === "string" && err.payload.error.trim()) {
    return err.payload.error.trim();
  }
  const payload = err?.payload?.error;
  const message = typeof payload === "string" ? payload.trim() : "";
  const machineShaped = /^[[{<]/.test(message) || /\n/.test(message) || message.length > 180;
  if (!message || machineShaped) {
    if (reason) console.error("[protopal] request failed:", reason);
    return fallback;
  }
  return message;
}
